import type { ReactNode } from 'react'
import { CircleAlert, CircleCheck, CircleDashed } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  PackagingItemAuditIssue,
  PackagingItemAuditStatus,
} from '@/shared/hooks/api/packagingItemsOverview'
import { auditIssueLabelKey } from './packagingSupplierOverviewColumns'
import { formatPackagingItemAuditRunTimestamp } from './formatPackagingItemAuditRunTimestamp'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'

type PackagingItemAuditCellProps = {
  auditStatus: PackagingItemAuditStatus
  auditIssues: PackagingItemAuditIssue[]
  auditLastRunAt?: string | null
}

function AuditTableHoverCell({
  ariaLabel,
  icon,
  children,
}: {
  ariaLabel: string
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          aria-label={ariaLabel}
          onClick={(event) => event.stopPropagation()}
        >
          {icon}
        </button>
      </HoverCardTrigger>
      <HoverCardContent className='w-80 max-w-sm p-4' align='start' side='top'>
        {children}
      </HoverCardContent>
    </HoverCard>
  )
}

export function PackagingItemAuditCell({
  auditStatus,
  auditIssues,
  auditLastRunAt,
}: PackagingItemAuditCellProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const useChineseReasoning = i18n.language.startsWith('zh')

  if (auditStatus === 'not_audited') {
    const ariaLabel = t('list.audit.notAudited')
    return (
      <AuditTableHoverCell
        ariaLabel={ariaLabel}
        icon={
          <CircleDashed className='size-4 text-muted-foreground' aria-hidden />
        }
      >
        <p className='text-sm text-popover-foreground'>{ariaLabel}</p>
      </AuditTableHoverCell>
    )
  }

  if (auditStatus === 'clear') {
    const lastRunLabel =
      auditLastRunAt != null && auditLastRunAt !== ''
        ? formatPackagingItemAuditRunTimestamp(
            auditLastRunAt,
            i18n.language
          )
        : '—'
    return (
      <AuditTableHoverCell
        ariaLabel={lastRunLabel}
        icon={
          <CircleCheck className='size-4 text-green-600 dark:text-green-500' aria-hidden />
        }
      >
        <p className='text-sm text-popover-foreground'>{lastRunLabel}</p>
      </AuditTableHoverCell>
    )
  }

  const summaryLabel = t('list.redFlags.hoverTitle')

  return (
    <AuditTableHoverCell
      ariaLabel={summaryLabel}
      icon={<CircleAlert className='size-4 text-destructive' aria-hidden />}
    >
      <ul className='space-y-3'>
        {auditIssues.map((issue) => (
          <li key={issue.code} className='flex items-start gap-2'>
            <CircleAlert
              className='mt-0.5 size-4 shrink-0 text-destructive'
              aria-hidden
            />
            <div className='min-w-0 space-y-0.5'>
              <p className='text-sm font-medium text-popover-foreground'>
                {t(auditIssueLabelKey(issue.code))}
              </p>
              <p className='text-sm text-muted-foreground'>
                {useChineseReasoning ? issue.reasoningZh : issue.reasoningEn}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </AuditTableHoverCell>
  )
}
